/**
 * Shared constants and name mappings for regions, clouds, and locations.
 * This is the single source of truth for all location-related definitions.
 */

import emojiFlags from 'emoji-flags';
import COUNTRY_CODES from './country-codes';

// Region type constants
export const CONTINENT_CODES = [
  'Continent:NA',
  'Continent:AF',
  'Continent:EU',
  'Continent:OC',
  'Continent:SA',
  'Continent:AS',
];

export const CLOUD_CODES = ['aws', 'azr', 'goo', 'sft', 'acc'];

export const COUNTRY_PREFIX = 'Country:';

// Display names for regions (continents)
export const REGION_NAMES = {
  all: 'Worldwide',
  'Continent:NA': 'North America',
  'Continent:AF': 'Africa',
  'Continent:EU': 'Europe',
  'Continent:OC': 'Oceania',
  'Continent:SA': 'South America',
  'Continent:AS': 'Asia',
};

// Display names for regions with emoji (for dropdowns and formatted display)
export const REGION_NAMES_WITH_EMOJI = {
  'Continent:AF': '\u{1F30D} Africa',
  'Continent:AN': '\u{1F310} Antarctica',
  'Continent:AS': '\u{1F30F} Asia',
  'Continent:EU': '\u{1F30D} Europe',
  'Continent:NA': '\u{1F30E} North America',
  'Continent:OC': '\u{1F30F} Oceania',
  'Continent:SA': '\u{1F30E} South America',
};

// Display names for cloud providers
export const CLOUD_NAMES = {
  aws: 'Amazon AWS',
  azr: 'Microsoft Azure',
  goo: 'Google Cloud',
  sft: 'IBM Cloud',
  acc: 'Akamai Connected Cloud',
};

/**
 * Get display name for a region code
 * @param {string} code - Region code (e.g., 'Continent:NA', 'Country:US', 'all')
 * @param {boolean} withEmoji - Whether to include emoji in the name
 * @returns {string} Display name or the original code if not found
 */
export function getRegionName(code, withEmoji = false) {
  if (code.startsWith(COUNTRY_PREFIX)) {
    const countryCode = code.slice(COUNTRY_PREFIX.length);
    const countryData = COUNTRY_CODES.find((c) => c.id === countryCode);
    const countryName = countryData?.text || countryCode;

    if (withEmoji) {
      const flagEmoji = emojiFlags.countryCode(countryCode)?.emoji;
      return flagEmoji ? `${flagEmoji} ${countryName}` : countryName;
    }

    return countryName;
  }

  if (withEmoji && REGION_NAMES_WITH_EMOJI[code]) {
    return REGION_NAMES_WITH_EMOJI[code];
  }

  if (REGION_NAMES[code]) {
    return REGION_NAMES[code];
  }

  return code;
}

/**
 * Get display name for a cloud code
 * @param {string} code - Cloud code (e.g., 'aws', 'azr')
 * @returns {string} Display name or the original code if not found
 */
export function getCloudName(code) {
  return CLOUD_NAMES[code] || code;
}

/**
 * Check if a code is a continent code
 * @param {string} code - Code to check
 * @returns {boolean}
 */
export function isContinent(code) {
  return CONTINENT_CODES.includes(code);
}

/**
 * Check if a code is a cloud code
 * @param {string} code - Code to check
 * @returns {boolean}
 */
export function isCloud(code) {
  return CLOUD_CODES.includes(code);
}

/**
 * Check if a code is a country code
 * @param {string} code - Code to check
 * @returns {boolean}
 */
export function isCountry(code) {
  return code.startsWith(COUNTRY_PREFIX);
}
